import { Box, Center, HStack, Image, VStack } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

import Text from 'components/Text';
import { ERoute } from 'types/frontend';

import {
  getOverallScoreColor,
  getRoastDisplayName,
  type RoastData,
} from '../utils';

const SCORE_CIRCLE_COLORS: Record<string, string> = {
  'red.400': '#fc8181',
  'orange.400': '#f6ad55',
  'yellow.400': '#f6e05e',
  'green.400': '#68d391',
};

const RANK_BADGES: Record<number, { emoji: string; bg: string; border: string; color: string }> = {
  1: { emoji: '🏆', bg: 'rgba(234,179,8,0.15)', border: 'rgba(234,179,8,0.2)', color: '#facc15' },
  2: { emoji: '🥈', bg: 'rgba(156,163,175,0.15)', border: 'rgba(156,163,175,0.2)', color: '#d1d5db' },
  3: { emoji: '🥉', bg: 'rgba(194,120,3,0.15)', border: 'rgba(194,120,3,0.2)', color: '#f59e0b' },
};

interface RoastListCardProps {
  roast: RoastData;
  isMine?: boolean;
  rank?: number;
}

const RoastListCard = ({ roast, isMine, rank }: RoastListCardProps) => {
  const navigate = useNavigate();
  const overallScore = roast.scores?.overall ?? null;
  const isLive = roast.status === 'ready';
  const isProcessing = roast.status === 'processing';
  const displayName = getRoastDisplayName(roast);
  const scoreColorToken = overallScore !== null ? getOverallScoreColor(overallScore) : 'gray.400';
  const strokeColor = SCORE_CIRCLE_COLORS[scoreColorToken] ?? '#a0aec0';

  const rankParam = rank && rank <= 3 ? `?rank=${rank}` : '';
  const goToDetail = () => navigate(`${ERoute.Roasts}/${roast.id}${rankParam}`);
  const goToComments = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`${ERoute.Roasts}/${roast.id}?tab=comments${rank && rank <= 3 ? `&rank=${rank}` : ''}`);
  };

  return (
    <Box
      borderRadius="xl"
      border="1px solid"
      borderColor="whiteAlpha.100"
      backgroundColor="whiteAlpha.50"
      overflow="hidden"
      transition="all 0.2s"
      _hover={{ borderColor: 'rgba(249,115,22,0.15)', backgroundColor: 'whiteAlpha.100' }}
      cursor="pointer"
      onClick={goToDetail}
      width="100%"
    >
      <HStack spacing={0} alignItems="stretch">
        {/* Screenshot thumbnail */}
        <Box
          width="160px"
          minWidth="160px"
          backgroundColor="whiteAlpha.50"
          overflow="hidden"
          display={{ base: 'none', md: 'block' }}
        >
          {roast.screenshot_url ? (
            <Image
              src={roast.screenshot_url}
              alt={displayName}
              width="100%"
              height="100%"
              objectFit="cover"
              minHeight="110px"
            />
          ) : (
            <Center height="100%" minHeight="110px">
              <Text fontSize="xLarge" opacity="tertiary">
                {isProcessing ? '...' : '?'}
              </Text>
            </Center>
          )}
        </Box>

        {/* Content */}
        <VStack spacing={1.5} p={{ base: 4, md: 5 }} alignItems="flex-start" flex={1} minWidth={0}>
          <HStack spacing={2.5} width="100%">
            {roast.site_logo_url ? (
              <Box
                width="28px"
                height="28px"
                borderRadius="lg"
                border="1px solid"
                borderColor="whiteAlpha.100"
                backgroundColor="whiteAlpha.50"
                overflow="hidden"
                flexShrink={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                p="4px"
              >
                <Image
                  src={roast.site_logo_url}
                  alt=""
                  width="100%"
                  height="100%"
                  objectFit="contain"
                  borderRadius="sm"
                />
              </Box>
            ) : (
              <Box
                width="28px"
                height="28px"
                borderRadius="lg"
                bgGradient="linear(to-br, orange.500, red.500)"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <Box as="span" style={{ fontSize: '12px', fontWeight: 700, color: 'white', lineHeight: 1 }}>
                  {displayName.charAt(0).toUpperCase()}
                </Box>
              </Box>
            )}
            <HStack spacing={2} minWidth={0} flex={1}>
              <Text
                fontSize="medium"
                fontWeight="semibold"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {displayName}
              </Text>
              {rank && rank <= 3 && RANK_BADGES[rank] && (
                <Box
                  px={1.5}
                  py={0.5}
                  borderRadius="full"
                  backgroundColor={RANK_BADGES[rank].bg}
                  border="1px solid"
                  borderColor={RANK_BADGES[rank].border}
                  flexShrink={0}
                  display="flex"
                  alignItems="center"
                  gap="3px"
                >
                  <Box as="span" style={{ fontSize: '10px', lineHeight: 1 }}>{RANK_BADGES[rank].emoji}</Box>
                  <Box as="span" style={{ fontSize: '10px', fontWeight: 700, color: RANK_BADGES[rank].color }}>
                    #{rank}
                  </Box>
                </Box>
              )}
            </HStack>
            {roast.is_promoted && (
              <Box
                px={1.5}
                py={0.5}
                borderRadius="full"
                backgroundColor="rgba(139,92,246,0.15)"
                border="1px solid"
                borderColor="rgba(139,92,246,0.2)"
                flexShrink={0}
              >
                <Box as="span" style={{ fontSize: '10px', fontWeight: 700, color: '#c4b5fd' }}>
                  PROMOTED
                </Box>
              </Box>
            )}
            {isProcessing && (
              <Box
                px={2}
                py={0.5}
                borderRadius="full"
                backgroundColor="rgba(249,115,22,0.1)"
                border="1px solid"
                borderColor="rgba(249,115,22,0.15)"
                flexShrink={0}
                display="flex"
                alignItems="center"
                gap="4px"
              >
                <Box
                  width="5px"
                  height="5px"
                  borderRadius="full"
                  backgroundColor="orange.400"
                  className="pulse-dot"
                />
                <Box as="span" style={{ fontSize: '10px', fontWeight: 700, color: '#fb923c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Analyzing
                </Box>
              </Box>
            )}
          </HStack>

          {roast.summary && (
            <Text
              fontSize="small"
              opacity="secondary"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              maxWidth="100%"
            >
              {roast.summary}
            </Text>
          )}

          <HStack width="100%" alignItems="center" spacing={3}>
            <HStack
              spacing={1.5}
              px={2.5}
              py={1}
              borderRadius="md"
              border="1px solid"
              borderColor="whiteAlpha.50"
              backgroundColor="whiteAlpha.50"
              _hover={{ backgroundColor: 'whiteAlpha.200' }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                goToDetail();
              }}
              cursor="pointer"
              transition="all 0.15s"
            >
              <Box as="span" style={{ fontSize: '11px', lineHeight: 1 }}>▲</Box>
              <Text fontSize="micro" opacity="secondary" fontWeight="medium">
                {roast.upvote_count ?? 0}
              </Text>
              <Text fontSize="micro" opacity="tertiary">
                Upvotes
              </Text>
            </HStack>
            <HStack
              spacing={1.5}
              px={2.5}
              py={1}
              borderRadius="md"
              border="1px solid"
              borderColor="whiteAlpha.50"
              backgroundColor="whiteAlpha.50"
              _hover={{ backgroundColor: 'whiteAlpha.200' }}
              onClick={goToComments}
              cursor="pointer"
              transition="all 0.15s"
            >
              <Box as="span" style={{ fontSize: '11px', lineHeight: 1 }}>💬</Box>
              <Text fontSize="micro" opacity="secondary" fontWeight="medium">
                {roast.comment_count ?? 0}
              </Text>
              <Text fontSize="micro" opacity="tertiary">
                Comments
              </Text>
            </HStack>
            <HStack spacing={2} display={{ base: 'none', sm: 'flex' }} alignItems="center" ml="auto">
              {roast.category && roast.category !== 'other' && (
                <Text fontSize="micro" opacity="tertiary" textTransform="capitalize">
                  {roast.category}
                </Text>
              )}
              {roast.category && roast.category !== 'other' && roast.submitted_at && (
                <Box as="span" style={{ color: 'rgba(255,255,255,0.1)' }}>|</Box>
              )}
              {roast.submitted_at && (() => {
                const isReRoasted = roast.updated_at && roast.submitted_at &&
                  (new Date(roast.updated_at).getTime() - new Date(roast.submitted_at).getTime() > 86400000);
                const dateStr = isReRoasted
                  ? new Date(roast.updated_at!).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : new Date(roast.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                return (
                  <Text fontSize="micro" opacity="tertiary">
                    {dateStr}
                  </Text>
                );
              })()}
            </HStack>
          </HStack>
        </VStack>

        {/* Score circle — only for ready roasts */}
        {isLive && overallScore !== null && (
          <Center
            px={4}
            display={{ base: 'none', sm: 'flex' }}
            flexShrink={0}
          >
            <Box position="relative" width="56px" height="56px">
              <svg
                width="56"
                height="56"
                viewBox="0 0 48 48"
                style={{ transform: 'rotate(-90deg)' }}
              >
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  fill="none"
                  stroke="rgba(255,255,255,0.06)"
                  strokeWidth="3"
                />
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  fill="none"
                  stroke={strokeColor}
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={`${(overallScore / 100) * 125.6} 125.6`}
                />
              </svg>
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text fontSize="small" fontWeight="bold" color={scoreColorToken}>
                  {overallScore}%
                </Text>
              </Box>
            </Box>
          </Center>
        )}
      </HStack>
    </Box>
  );
};

export default RoastListCard;
